import { createElement } from 'react';
import { useParams } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';

import Alert from '@components/common/Alert';
import Loader from '@components/common/Loader';
import useShuttle from '@hooks/useShuttle';
import { queryClient } from '@lib/common/queryClient';
import { SHUTTLE_REGISTRY } from '@lib/common/shuttleRegistry';

import '@styles/preload/index.scss';
import { ModalContextProvider } from '@contexts/ModalContext';
import { ModalContextProviderV2 } from '@contexts/ModalContextV2';

const CORE_APP_NAME_MAP = {
  Election            : 'guaranteed-income-direct',
  GidNoSSO            : 'guaranteed-income-direct',
  Ssga                : 'income-wise',
  SsgaNoSSO           : 'income-wise',
  MetlifeIiaQuoting   : 'metlife-iia-quoting',
  MetlifeIiaEducation : 'metlife-iia-education',
  MetlifeIiaElection  : 'metlife-iia-election',
  Jpm                 : 'jpm',
  JpmNoSSO            : 'jpm',
  Demo                : 'demo',
  Trlit               : 'trlit',
  RicAlightQuoting    : 'ric-alight-quoting',
  RicAlightEducation  : 'ric-alight-education',
  RicAlightElection   : 'ric-alight-election',
};

function ShuttleCore() {
  const { shuttleRootPath, planId } = useParams();
  const { isLoading, data, error, setError } = useShuttle(shuttleRootPath, planId);

  window.shuttleRootPath = shuttleRootPath;

  const core = data?.config?.core;
  const themeKey = data?.config?.theme_key || CORE_APP_NAME_MAP[core];
  let currentView = null;

  if (isLoading) {
    currentView = <Loader message="Preparing your experience..." />;
  }

  // TODO: This error component must be a custom Micruity error. An error at this level doesn't belong to any app.
  if (Object.keys(error).length > 0) {
    currentView = (
      <div className="global__error">
        <Alert
          variant="danger"
          message={(
            <>
              <h4>{error.heading}</h4>
              <span>{error.description}</span>
            </>
          )}
        />
      </div>
    );
  } else if (core) {
    currentView = createElement(SHUTTLE_REGISTRY[core], { shuttleData : data, setGlobalError: setError });
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ModalContextProvider>
        <ModalContextProviderV2>
          <div
            className={`global${themeKey ? ` global--${themeKey}` : ''}`}
            id={themeKey || undefined}
          >
            {currentView}
          </div>
        </ModalContextProviderV2>
      </ModalContextProvider>
    </QueryClientProvider>
  );
}

export default ShuttleCore;

// Copyright © 2025 Micruity Inc.
